import { useState, type ReactNode } from 'react'
import {
  Eye, ExternalLink, Heart, ImageIcon, Link2, LogIn, MessageCircle, PieChart,
  Play, Sparkles, UserPlus, Users,
} from 'lucide-react'
import { Link } from '../router.js'
import { LiveNumber, QuietNumber } from '../soirees/lib.js'
import { mediaFileUrl, useInstagram, metrique, type ContentReading, type MetricReading } from './InstagramData.js'
import { InsightsChart } from './InstagramChart.js'
import { deltaClasse, deltaTexte, fraicheurFr, nombreFr, type HonestDelta } from './lib.js'

/**
 * WIDGETS /communication/instagram — chaque tuile dit sa VÉRITÉ :
 *   · valeur présente (y compris un vrai 0) → chiffre + delta honnête ;
 *   · dataset vide / métrique refusée → « Donnée non disponible » (§41-42) ;
 *   · pas d'historique comparable → « historique en construction », pas +0 %.
 * Aucune donnée n'est demandée à Meta au rendu : PostgreSQL uniquement.
 */

/* ─────────────────────────── briques ─────────────────────────── */

function Carte({ tint, children, className = '' }: { tint: string; children: ReactNode; className?: string }) {
  return (
    <div className={`lc-card lc-card--${tint} cm-widget ${className}`}>
      <span className="lc-card__edge" aria-hidden="true" />
      {children}
    </div>
  )
}

/** Miniature LOCALE (archive Gestion) — jamais le CDN Meta (§127). */
function ApercuLocal({ c, portrait = false }: { c: ContentReading; portrait?: boolean }) {
  if (c.archive.preview_asset_id) {
    return <img src={mediaFileUrl(c.archive.preview_asset_id)} alt="" loading="lazy"
      className={portrait ? 'igw-thumb igw-thumb--portrait' : 'igw-thumb'} />
  }
  return (
    <span className={`cm-contentlist__ph ${portrait ? 'igw-thumb--portrait' : ''}`} aria-hidden="true">
      {c.kind === 'REEL' ? <Play size={14} /> : <ImageIcon size={14} />}
    </span>
  )
}

function Tete({ Icon, titre, sub }: { Icon: typeof Eye; titre: string; sub?: ReactNode }) {
  return (
    <header className="cm-widget__head">
      <span className="lc-card__icon" aria-hidden="true"><Icon /></span>
      <div>
        <h3>{titre}</h3>
        {sub ? <span className="cm-widget__sub">{sub}</span> : null}
      </div>
    </header>
  )
}

function DeltaBadge({ delta, discret = false }: { delta: HonestDelta | null; discret?: boolean }) {
  const t = deltaTexte(delta)
  if (t) {
    return (
      <span className={`ig-delta ig-delta--${deltaClasse(delta)} ${discret ? 'ig-delta--discret' : ''}`}>
        {t.abs}{t.pct ? <em>{t.pct}</em> : null}
      </span>
    )
  }
  if (delta?.state === 'NO_HISTORY') {
    return <span className="ig-delta ig-delta--none">historique en construction{delta.availableInDays !== null ? ` · ${delta.availableInDays} j` : ''}</span>
  }
  return null
}

/** La lecture d'une métrique — EMPTY ≠ 0, affiché tel quel (§42).
 *
 * `reveal` : les GRANDES valeurs (hero, cm-big, parcours) empruntent la
 * signature du produit — `LiveNumber`, la vraie valeur immédiatement, jamais
 * un compteur 0 → n. Les lignes secondaires restent en fondu discret
 * (`QuietNumber`) : une carte où tout bouge ne dit plus où regarder.
 * Un état EMPTY / non disponible n'a JAMAIS droit à la révélation de chiffre. */
function ValeurMetrique({ m, reveal = false }: { m: MetricReading | null; reveal?: boolean }) {
  if (!m || m.availability === 'NO_DATA') {
    return <span className="ig-na" title="Aucun relevé pour cette période — la donnée arrivera avec les synchronisations.">—</span>
  }
  if (m.availability !== 'AVAILABLE' || m.value === null) {
    return (
      <span className="ig-na" title={m.detail ?? 'Meta ne renvoie pas cette donnée pour ce compte ou cette période.'}>
        non disponible
      </span>
    )
  }
  return reveal
    ? <LiveNumber value={m.value} format={nombreFr} />
    : <QuietNumber value={m.value} format={nombreFr} />
}

const SOURCE_FR = { INSTAGRAM_INSIGHTS: 'Instagram Insights', GESTION_HISTORY: 'Historique Gestion' } as const

function LigneKpi({ m }: { m: MetricReading | null }) {
  return (
    <li title={m ? `Source : ${SOURCE_FR[m.source]}` : undefined}>
      <span className="cm-actionlist__label">{m?.label ?? '…'}</span>
      <strong><ValeurMetrique m={m} /></strong>
      {m?.delta && m.availability === 'AVAILABLE' ? <DeltaBadge delta={m.delta} discret /> : null}
    </li>
  )
}

function PasConnecte({ texte }: { texte: string }) {
  return (
    <div className="cm-empty">
      <span className="cm-empty__icon" aria-hidden="true"><LogIn size={22} /></span>
      <strong>Connexion propriétaire requise</strong>
      <p>{texte}</p>
      <Link href="/communication/integrations/instagram" className="lc-btn">Connecter Instagram</Link>
    </div>
  )
}

/* ═══════════ 1 · AUDIENCE — la grande pièce (§61) ═══════════ */

export function IgAudienceWidget() {
  const { data, periode } = useInstagram()
  const a = data?.audience
  const libelle = periode === 'THIS_WEEK' ? 'cette semaine'
    : periode === 'TODAY' ? 'aujourd’hui'
    : periode === 'SEVEN_DAYS' ? 'sur 7 jours'
    : periode === 'THIRTY_DAYS' ? 'sur 30 jours' : 'sur 90 jours'
  return (
    <Carte tint="violet" className="ig-hero">
      <Tete Icon={Users} titre="Audience"
        sub={data?.lastSyncAt ? `synchronisé ${fraicheurFr(data.lastSyncAt)}` : null} />
      {a?.followers === null || !a ? (
        <p className="lc-hint">Aucun relevé d’abonnés pour l’instant.</p>
      ) : (
        <>
          <div className="ig-hero__num">
            <strong className="ig-hero__value"><LiveNumber value={a.followers} format={nombreFr} /></strong>
            <span className="ig-hero__label">ABONNÉS</span>
            <div className="ig-hero__delta">
              <DeltaBadge delta={a.delta} />
              <span className="ig-hero__periode">{libelle.toUpperCase()}</span>
            </div>
          </div>
          <InsightsChart points={a.series.slice(-120)} height={150} />
          {data.follows.gained !== null || data.follows.lost !== null ? (
            <div className="cm-splitline">
              {data.follows.gained !== null ? <span><UserPlus size={13} aria-hidden="true" /> {nombreFr(data.follows.gained)} nouveaux</span> : null}
              {data.follows.lost !== null ? <span>{nombreFr(data.follows.lost)} désabonnements</span> : null}
              {data.follows.gained !== null && data.follows.lost !== null
                ? <span>solde {data.follows.gained - data.follows.lost >= 0 ? '+' : ''}{nombreFr(data.follows.gained - data.follows.lost)}</span> : null}
            </div>
          ) : null}
        </>
      )}
    </Carte>
  )
}

/* ═══════════ 2 · VUES & REACH — jamais mélangés (§62) ═══════════ */

export function IgReachViewsWidget({ config }: { config?: Record<string, unknown> }) {
  const { data } = useInstagram()
  const tete = config?.['headline'] === 'reach' ? 'reach' : 'views'
  const principal = metrique(data, tete === 'reach' ? 'reach' : 'views')
  const secondaire = metrique(data, tete === 'reach' ? 'views' : 'reach')
  if (data && !data.connected) {
    return <Carte tint="cyan"><Tete Icon={Eye} titre="Vues & Portée" />
      <PasConnecte texte="Vues et comptes atteints exigent l’autorisation du compte — la lecture publique ne les expose pas." /></Carte>
  }
  return (
    <Carte tint="cyan">
      <Tete Icon={Eye} titre={principal?.label ?? 'Vues'}
        sub={principal ? `Source : ${SOURCE_FR[principal.source]}` : null} />
      <div className="cm-big">
        <strong className="cm-big__num"><ValeurMetrique m={principal} reveal /></strong>
      </div>
      {principal?.delta && principal.availability === 'AVAILABLE' ? <DeltaBadge delta={principal.delta} /> : null}
      <ul className="cm-actionlist" style={{ marginTop: 'auto' }}>
        <LigneKpi m={secondaire} />
        <LigneKpi m={metrique(data, 'accounts_engaged')} />
      </ul>
      <p className="lc-hint ig-def">
        Vues : nombre de fois où vos contenus ont été affichés ou lus.
        Comptes atteints : comptes UNIQUES ayant vu au moins un contenu — les
        deux ne s’additionnent pas.
      </p>
    </Carte>
  )
}

/* ═══════════ 3 · INTERACTIONS — la composition (§63) ═══════════ */

export function IgInteractionsWidget() {
  const { data } = useInstagram()
  const total = metrique(data, 'total_interactions')
  if (data && !data.connected) {
    return <Carte tint="violet"><Tete Icon={Heart} titre="Interactions" />
      <PasConnecte texte="Likes, commentaires, partages et sauvegardes détaillés arrivent avec la connexion propriétaire." /></Carte>
  }
  return (
    <Carte tint="violet">
      <Tete Icon={Heart} titre="Interactions" sub={total ? `Source : ${SOURCE_FR[total.source]}` : null} />
      <div className="cm-big">
        <strong className="cm-big__num"><ValeurMetrique m={total} reveal /></strong>
        <span className="cm-big__label">interactions</span>
      </div>
      {total?.delta && total.availability === 'AVAILABLE' ? <DeltaBadge delta={total.delta} /> : null}
      <ul className="cm-actionlist" style={{ marginTop: 'auto' }}>
        {['likes', 'comments', 'shares', 'saves', 'replies'].map((k) => <LigneKpi key={k} m={metrique(data, k)} />)}
      </ul>
    </Carte>
  )
}

/* ═══════════ 4 · ACTIVITÉ DU PROFIL (§51) + PARCOURS DE VISIBILITÉ (§36-38) ═══ */

export function IgProfileWidget() {
  const { data } = useInstagram()
  if (data && !data.connected) {
    return <Carte tint="neutral"><Tete Icon={Link2} titre="Activité du profil" />
      <PasConnecte texte="Visites du profil et clics sur vos liens exigent l’autorisation du compte." /></Carte>
  }
  const reach = metrique(data, 'reach')
  const visites = metrique(data, 'profile_views')
  const liens = metrique(data, 'profile_links_taps')
  const gained = data?.follows.gained ?? null
  const lost = data?.follows.lost ?? null
  // Ratio DESCRIPTIF, même période, populations agrégées — jamais présenté
  // comme une conversion garantie (§37-38).
  const ratio = reach?.value && visites?.value !== null && visites?.value !== undefined && reach.value > 0
    ? (visites.value / reach.value) * 100 : null
  return (
    <Carte tint="neutral">
      <Tete Icon={Link2} titre="Activité du profil" />
      <div className="igp-parcours" title="Agrégats de même période — pas un suivi individuel.">
        <span className="igp-parcours__titre">DE LA PORTÉE AU PROFIL</span>
        <div className="igp-etapes lc-stagger">
          <div className="igp-etape">
            <strong><ValeurMetrique m={reach} reveal /></strong>
            <span title="Comptes uniques ayant vu au moins un contenu sur la période.">comptes atteints</span>
          </div>
          <span className="igp-fleche" aria-hidden="true">→</span>
          <div className="igp-etape">
            <strong><ValeurMetrique m={visites} reveal /></strong>
            <span>visites du profil</span>
          </div>
          <span className="igp-fleche" aria-hidden="true">→</span>
          <div className="igp-etape">
            <strong><ValeurMetrique m={liens} reveal /></strong>
            <span title="Tous les liens du profil (site, appel, e-mail…) — pas seulement le site.">clics sur les liens</span>
          </div>
        </div>
        {ratio !== null ? (
          <span className="lc-hint">Taux visites / comptes atteints : {ratio.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %</span>
        ) : null}
      </div>
      <ul className="cm-actionlist" style={{ marginTop: 'auto' }}>
        {gained !== null ? <li><span className="cm-actionlist__label">Nouveaux abonnements</span><strong>{nombreFr(gained)}</strong></li> : null}
        {lost !== null ? <li><span className="cm-actionlist__label">Désabonnements</span><strong>{nombreFr(lost)}</strong></li> : null}
        {gained !== null && lost !== null
          ? <li><span className="cm-actionlist__label">Solde</span><strong>{gained - lost >= 0 ? '+' : ''}{nombreFr(gained - lost)}</strong></li> : null}
        <LigneKpi m={metrique(data, 'reposts')} />
      </ul>
    </Carte>
  )
}

/* ═══════════ 4bis · COMMENTAIRES RÉCENTS (§27) ═══════════ */

export function IgCommentsWidget() {
  const { data } = useInstagram()
  const commentaires = data?.recentComments ?? []
  return (
    <Carte tint="violet">
      <Tete Icon={MessageCircle} titre="Commentaires récents" />
      {commentaires.length === 0 ? (
        <p className="lc-hint">
          {data?.metricSupport['comments']?.state === 'UNAVAILABLE'
            ? 'Reconnectez Instagram pour autoriser la lecture des commentaires.'
            : 'Aucun commentaire synchronisé pour l’instant.'}
        </p>
      ) : (
        <ul className="cm-actionlist igw-comments">
          {commentaires.map((c) => (
            <li key={c.id}>
              <div className="igw-comment">
                <span className="igw-comment__tete">
                  {c.author_username ? (
                    <a href={`https://www.instagram.com/${encodeURIComponent(c.author_username)}/`}
                      target="_blank" rel="noreferrer" className="igc-user">
                      @{c.author_username} <ExternalLink size={10} aria-hidden="true" />
                    </a>
                  ) : <span className="lc-hint">Compte inconnu</span>}
                  {c.content_id ? (
                    <Link href={`/communication/instagram/contenus/${c.content_id}`} className="lc-hint">
                      {c.content_kind === 'REEL' ? 'Reel' : c.content_kind === 'CAROUSEL_ALBUM' ? 'Carrousel' : 'Contenu'}
                    </Link>
                  ) : null}
                </span>
                <span className="igw-comment__texte">{c.text.length > 90 ? `${c.text.slice(0, 90)}…` : c.text}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Carte>
  )
}

/* ═══════════ 5 · TOP CONTENUS (§53) ═══════════ */

function scoreDe(c: ContentReading, tri: string): number | null {
  if (tri in c.metrics) return c.metrics[tri]!
  if (tri === 'likes' && c.like_count !== null) return c.like_count
  if (tri === 'comments' && c.comments_count !== null) return c.comments_count
  return null
}

const TRIS_CONTENU: { key: string; label: string }[] = [
  { key: 'views', label: 'Vues' },
  { key: 'reach', label: 'Portée' },
  { key: 'total_interactions', label: 'Interactions' },
  { key: 'shares', label: 'Partages' },
  { key: 'saved', label: 'Sauvegardes' },
  { key: 'comments', label: 'Commentaires' },
]

export function IgTopContentWidget({ config }: { config?: Record<string, unknown> }) {
  const { data } = useInstagram()
  const triCfg = String(config?.['tri'] ?? 'views')
  const items = data?.topContent ?? []
  const disponible = TRIS_CONTENU.filter((t) => items.some((c) => scoreDe(c, t.key) !== null))
  const tri = disponible.some((t) => t.key === triCfg) ? triCfg : (disponible[0]?.key ?? 'likes')
  const triees = [...items].sort((a, b) => (scoreDe(b, tri) ?? -1) - (scoreDe(a, tri) ?? -1)).slice(0, 5)
  return (
    <Carte tint="violet">
      <Tete Icon={Sparkles} titre="Top contenus"
        sub={disponible.length > 0 ? `par ${TRIS_CONTENU.find((t) => t.key === tri)?.label.toLowerCase() ?? tri}` : null} />
      {triees.length === 0 ? <p className="lc-hint">Aucun contenu relevé sur la période.</p> : (
        <ul className="cm-contentlist">
          {triees.map((c) => (
            <li key={c.id}>
              <Link href={`/communication/instagram/contenus/${c.id}`} className="cm-contentlist__row">
                <ApercuLocal c={c} />
                <span className="cm-contentlist__cap">
                  {c.kind === 'REEL' ? '▶ ' : ''}{c.caption ? c.caption.slice(0, 60) : (c.kind ?? 'Publication')}
                </span>
                <span className="cm-contentlist__stats">
                  {scoreDe(c, tri) !== null ? nombreFr(scoreDe(c, tri)!) : '—'}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      <Link href="/communication/instagram/contenus" className="lc-btn cm-widget__cta">Tous les contenus</Link>
    </Carte>
  )
}

/* ═══════════ 6 · REELS (§48-49) ═══════════ */

export function IgReelsWidget() {
  const { data } = useInstagram()
  const reels = data?.reels ?? []
  return (
    <Carte tint="cyan">
      <Tete Icon={Play} titre="Reels" />
      {reels.length === 0 ? <p className="lc-hint">Aucun Reel relevé.</p> : (
        <ul className="cm-contentlist">
          {reels.slice(0, 4).map((c) => (
            <li key={c.id}>
              <Link href={`/communication/instagram/contenus/${c.id}`} className="cm-contentlist__row">
                <ApercuLocal c={c} portrait />
                <span className="cm-contentlist__cap">{c.caption ? c.caption.slice(0, 52) : 'Reel'}</span>
                <span className="cm-contentlist__stats">
                  {typeof c.metrics['views'] === 'number' ? `${nombreFr(c.metrics['views'])} vues`
                    : typeof c.metrics['ig_reels_avg_watch_time'] === 'number' ? `${Math.round(c.metrics['ig_reels_avg_watch_time'] / 1000)} s moy.`
                    : c.like_count !== null ? `${nombreFr(c.like_count)} ♥` : '—'}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Carte>
  )
}

/* ═══════════ 7 · STORIES — capturées, jamais reconstruites (§50-52) ═══ */

export function IgStoriesWidget() {
  const { data } = useInstagram()
  const stories = data?.stories ?? []
  return (
    <Carte tint="amber">
      <Tete Icon={ImageIcon} titre="Stories" />
      {stories.length === 0 ? (
        <p className="lc-hint">
          Aucune Story capturée. Les Stories sont éphémères chez Meta : Gestion
          relève leurs statistiques PENDANT leurs 24 h de vie et les conserve
          ensuite — rien n’est reconstruit après coup.
        </p>
      ) : (
        <div className="igw-stories">
          {stories.slice(0, 4).map((c) => (
            <Link key={c.id} href={`/communication/instagram/contenus/${c.id}`} className="igw-story">
              <span className="igw-story__media">
                {c.archive.preview_asset_id
                  ? <img src={mediaFileUrl(c.archive.preview_asset_id)} alt="" loading="lazy" />
                  : <span className="igw-story__ph"><ImageIcon size={16} aria-hidden="true" /></span>}
              </span>
              <span className="igw-story__stats">
                {c.published_at ? new Date(c.published_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '?'}
                <strong>{typeof c.metrics['views'] === 'number' ? `${nombreFr(c.metrics['views'])} vues` : typeof c.metrics['reach'] === 'number' ? `${nombreFr(c.metrics['reach'])} att.` : '—'}</strong>
              </span>
            </Link>
          ))}
        </div>
      )}
    </Carte>
  )
}

/* ═══════════ 8 · DÉMOGRAPHIE (§65) ═══════════ */

const DEMO_BREAKDOWN_FR: Record<string, string> = { age: 'Âge', gender: 'Genre', city: 'Ville', country: 'Pays' }
const GENRE_FR: Record<string, string> = { 'F': 'Femmes', 'M': 'Hommes', 'U': 'Non précisé' }

const POPULATIONS_FR = { follower: 'Abonnés', reached: 'Atteints', engaged: 'Engagés' } as const

export function IgDemographicsWidget({ config }: { config?: Record<string, unknown> }) {
  const { data } = useInstagram()
  const d = data?.demographics
  const popCle = ['follower', 'reached', 'engaged'].includes(String(config?.['population']))
    ? String(config?.['population']) as 'follower' | 'reached' | 'engaged' : 'follower'
  const [pop, setPop] = useState<'follower' | 'reached' | 'engaged'>(popCle)
  const support = data?.metricSupport[
    pop === 'follower' ? 'follower_demographics'
      : pop === 'reached' ? 'reached_audience_demographics' : 'engaged_audience_demographics']
  const breakdown = String(config?.['breakdown'] ?? 'age')
  const population = d?.[pop]
  if (!d || !population || !population[breakdown]) {
    return (
      <Carte tint="neutral">
        <Tete Icon={PieChart} titre="Audience — démographie" />
        <p className="lc-hint">
          {support?.state === 'UNAVAILABLE'
            ? 'Indisponible pour ce compte ou cette période — seuils Meta (100 abonnés minimum, restrictions de confidentialité). Ce n’est pas une panne.'
            : 'Pas encore relevée — la démographie est collectée une fois par jour.'}
        </p>
      </Carte>
    )
  }
  const entrees = Object.entries(population[breakdown]!).sort((a, b) => b[1] - a[1]).slice(0, 7)
  const total = entrees.reduce((s, [, v]) => s + v, 0) || 1
  return (
    <Carte tint="neutral">
      <Tete Icon={PieChart} titre="Audience — démographie"
        sub={`${POPULATIONS_FR[pop]} · ${DEMO_BREAKDOWN_FR[breakdown] ?? breakdown} · agrégats Meta (${d.timeframe.replace('last_', '').replace('_days', ' j')})`} />
      <div className="lc-seg igp-popseg" role="group" aria-label="Population">
        {(['follower', 'reached', 'engaged'] as const).map((k) => (
          <button key={k} type="button"
            className={`lc-seg__btn ${pop === k ? 'lc-seg__btn--on' : ''}`}
            aria-pressed={pop === k} onClick={() => setPop(k)}>{POPULATIONS_FR[k]}</button>
        ))}
      </div>
      <ul className="ig-demo">
        {entrees.map(([cle, v]) => (
          <li key={cle}>
            <span className="ig-demo__label">{GENRE_FR[cle] ?? cle}</span>
            <span className="ig-demo__bar"><span style={{ width: `${Math.round((v / total) * 100)}%` }} /></span>
            <strong>{nombreFr(v)}</strong>
          </li>
        ))}
      </ul>
    </Carte>
  )
}
